import { DownloadOutlined } from '@ant-design/icons';
import { Alert, Button, Checkbox, Drawer, Input, Table, Tag, Tooltip, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { extractErrorMessage, parseFilename, triggerDownload } from 'pages/attendance-manager/component/export-all-modal';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { AgencyRow, CreatorFeeRow } from '../types';
import { filterCreators, hoursText, num, pctText, ruleLabel, vnd } from '../utils';
import styles from '../style.module.scss';

interface Props {
  agency: AgencyRow | null;
  creators: CreatorFeeRow[];
  month: string;
  onClose: () => void;
}

const money = (v: number) => (v ? <span className={styles.money}>{num(v)}</span> : <span className={styles.zero}>0</span>);

/** Ngăn kéo một Agency: từng Creator, đạt nhiệm vụ nào, phí bao nhiêu. */
const AgencyDrawer = ({ agency, creators, month, onClose }: Props) => {
  const [q, setQ] = useState('');
  const [onlyFee, setOnlyFee] = useState(true);
  const [busy, setBusy] = useState(false);

  /** Bảng tính riêng của Agency này (vd Agency nhánh theo email Đại diện) để gửi / thanh toán. */
  const exportOne = async () => {
    if (!agency) return;
    setBusy(true);
    try {
      const response: any = await AxiosUtils.get(`${API.agencyFeeMonths}/${month}/export`, { agency: agency.key }, undefined, {
        responseType: 'blob',
        timeout: 120_000,
      });
      const filename = parseFilename(response?.headers?.['content-disposition'], `phi-agency-${month}.xlsx`);
      triggerDownload(
        new Blob([response.data], {
          type: response?.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        filename
      );
    } catch (err) {
      notification.error({ message: 'Không xuất được file', description: await extractErrorMessage(err) });
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    setQ('');
    setOnlyFee(true);
  }, [agency?.key]);

  const mine = useMemo(() => (agency ? creators.filter((c) => c.agencyKey === agency.key) : []), [agency, creators]);
  const rows = useMemo(() => filterCreators(mine, q, onlyFee), [mine, q, onlyFee]);

  const columns: ColumnsType<CreatorFeeRow> = [
    {
      title: 'Creator', key: 'creator', fixed: 'left', width: 180,
      render: (_, r) => (
        <div className={styles.creator}>
          <strong>@{r.tiktokUsername || r.uid || '—'}</strong>
          <span className={styles.sub}>{r.uid || 'Không còn UID'}</span>
          {(r.departed || r.vpnt || r.deducted || r.rule) && (
            <span className={styles.tags}>
              {r.rule && (
                <Tooltip title={r.ruleNote || undefined}>
                  <Tag color={r.rule === 'SHARE' ? 'orange' : 'purple'}>{ruleLabel(r)}</Tag>
                </Tooltip>
              )}
              {r.departed && <Tag>Đã rời đi</Tag>}
              {r.vpnt && <Tag color="red">VPNT</Tag>}
              {r.deducted && <Tag color="gold">Đã trừ ngày/giờ</Tag>}
            </span>
          )}
        </div>
      ),
    },
    { title: 'Gia nhập', dataIndex: 'joinedDay', width: 104, render: (v) => <span className={styles.money}>{v ? `${v.slice(8)}/${v.slice(5, 7)}/${v.slice(0, 4)}` : '—'}</span> },
    { title: 'Ngày', dataIndex: 'liveDays', width: 64, align: 'right' },
    { title: 'Giờ', key: 'hours', width: 90, align: 'right', render: (_, r) => hoursText(r.liveSeconds) },
    { title: 'KC', dataIndex: 'kc', width: 110, align: 'right', render: (v) => <span className={styles.money}>{num(v)}</span>, sorter: (a, b) => a.kc - b.kc },
    { title: 'Thu nhập quy đổi', dataIndex: 'incomeVnd', width: 112, align: 'right', render: (v) => <span className={styles.money}>{num(v)}</span> },
    { title: 'NV A', dataIndex: 'a', width: 96, align: 'right', render: money },
    { title: 'NV B', dataIndex: 'b', width: 84, align: 'right', render: money },
    { title: 'NV C', dataIndex: 'c', width: 84, align: 'right', render: money },
    {
      title: 'NV D', key: 'd', width: 160, align: 'right',
      render: (_, r) =>
        r.dTier && r.dMonthIndex ? (
          <Tooltip title={`${r.dTier} · tháng thứ ${r.dMonthIndex}: phí cố định ${vnd(r.dFee)} (tham khảo ≈ ${pctText(r.dRefPct)})`}>
            <div className={styles.dCell}>
              <Tag color="magenta" style={{ margin: 0 }}>{r.dTier} · T{r.dMonthIndex}</Tag>
              <span className={styles.money}>{num(r.dFee)}</span>
            </div>
          </Tooltip>
        ) : (
          <span className={styles.zero}>—</span>
        ),
    },
    {
      title: 'Phí Agency', dataIndex: 'fee', width: 120, align: 'right', defaultSortOrder: 'descend', sorter: (a, b) => a.fee - b.fee,
      render: (_, r) => (
        <div className={styles.dCell}>
          <span className={styles.feeTotal}>{num(r.fee)}</span>
          {r.companyKeep > 0 && (
            <Tooltip title={`Phí gốc ${vnd(r.feeGross)} · Agency nhận ${r.agencyPct}% · công ty giữ ${vnd(r.companyKeep)}`}>
              <span className={styles.keep}>cty giữ {num(r.companyKeep)}</span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Thưởng Creator', dataIndex: 'creatorReward', width: 106, align: 'right',
      render: (_, r) => (
        <div className={styles.dCell}>
          <span className={styles.creatorReward}>{num(r.creatorReward)}</span>
          {r.creatorDirect > 0 && <span className={styles.direct}>cty trả thẳng</span>}
        </div>
      ),
    },
  ];

  return (
    <Drawer
      width="min(1360px, 100vw)"
      open={!!agency}
      onClose={onClose}
      title={agency ? <span title={agency.name}>{agency.shortName}{agency.name !== agency.shortName ? ` · ${agency.name}` : ''}</span> : ''}
      extra={agency ? <Button icon={<DownloadOutlined />} loading={busy} onClick={exportOne}>Xuất Excel Agency này</Button> : null}
      destroyOnClose
    >
      {agency && (
        <>
          {agency.branchOf && (
            <Alert
              className={styles.section}
              type="info"
              showIcon
              message={`Agency nhánh trong Nhóm "${agency.branchOf}"`}
              description="Gồm các Creator có email Đại diện (cột E file TikTok) nằm trong bảng Email → Agency ở trang Chính sách. Phí và thưởng của các Creator này tính riêng ở đây, không còn nằm trong Nhóm gốc."
            />
          )}
          <div className={styles.statRow}>
            <div className={styles.stat}><div className={styles.statLabel}>Creator</div><div className={styles.statValue}>{num(agency.creators)}</div></div>
            <div className={styles.stat}><div className={styles.statLabel}>Tổng KC</div><div className={styles.statValue}>{num(agency.kc)}</div></div>
            <div className={styles.stat}><div className={styles.statLabel}>Phí NV A / B / C</div><div className={styles.statValue}>{num(agency.a)} / {num(agency.b)} / {num(agency.c)}</div></div>
            <div className={styles.stat}><div className={styles.statLabel}>Phí NV D</div><div className={styles.statValue}>{num(agency.d)}</div></div>
            <div className={styles.stat}><div className={styles.statLabel}>Tổng phí Agency</div><div className={styles.statValue}>{vnd(agency.fee)}</div></div>
            <div className={styles.stat}><div className={styles.statLabel}>Thưởng Creator</div><div className={styles.statValue}>{vnd(agency.creatorReward)}</div></div>
            <div className={styles.stat}>
              <div className={styles.statLabel}>Chuyển cho Agency</div>
              <div className={styles.statValue}>{vnd(agency.toAgency)}</div>
            </div>
            {(agency.creatorDirect > 0 || agency.companyKeep > 0) && (
              <div className={styles.stat}>
                <div className={styles.statLabel}>Cty trả thẳng Creator · Cty giữ</div>
                <div className={styles.statValue}>{num(agency.creatorDirect)} · {num(agency.companyKeep)}</div>
              </div>
            )}
          </div>
          <div className={styles.filters}>
            <Input.Search allowClear placeholder="Tìm @TikTok ID, UID, email" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 260 }} />
            <Checkbox checked={onlyFee} onChange={(e) => setOnlyFee(e.target.checked)}>Chỉ Creator có phí / thưởng</Checkbox>
            <span className={styles.muted}>{rows.length} / {mine.length} Creator</span>
          </div>
          <Table
            rowKey="key"
            size="small"
            columns={columns}
            dataSource={rows}
            scroll={{ x: 1312 }}
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} dòng` }}
            rowClassName={(r) => (r.vpnt ? styles.vpntRow : '')}
          />
        </>
      )}
    </Drawer>
  );
};

export default AgencyDrawer;
